"use client"

import { useEffect, useMemo, useState } from "react"
import { useField, useFormikContext } from "formik"
import { Check, ChevronsUpDown, Loader2, RotateCcw } from "lucide-react"

import { FormInput } from "@/components/form-inputs"
import {
  useDebouncedValue,
  useOpaqueCredentialSessionId,
} from "@/components/deliveries/providers/nova-post/nova-post-form-hooks"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDeliveryIntegrationSenderProfilesQuery } from "@/hooks/queries/delivery-integrations"
import type { DeliveryIntegrationConfig } from "@/lib/deliveries/config"
import { normalizeNovaPostProviderConfigInput } from "@/lib/deliveries/providers/nova-post/nova-post-defaults"
import type { DeliverySenderProfile } from "@/lib/deliveries/types"
import { cn } from "@/lib/utils"
import type { ListDeliveryIntegrationSenderProfilesInput } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


const API_KEY_DEBOUNCE_MS = 500

export function NovaPostSenderProfileField({
  apiKey,
  providerConfig,
  integrationId,
}: {
  apiKey: string
  providerConfig: DeliveryIntegrationConfig["providerConfig"]
  integrationId?: string
}) {
  const tUi = useTranslations("Deliveries.DeliveriesProvidersNovaPostNovaPostSenderProfileField")

  const { values, setFieldTouched, setFieldValue } = useFormikContext<{
    config: DeliveryIntegrationConfig
  }>()
  const [, nameMeta] = useField<string>("config.sender.name")
  const [, phoneMeta] = useField<string>("config.sender.phone")
  const [open, setOpen] = useState(false)
  const trimmedApiKey = apiKey.trim()
  const debouncedApiKey = useDebouncedValue(
    trimmedApiKey,
    API_KEY_DEBOUNCE_MS,
  )
  const hasPendingApiKey = trimmedApiKey !== debouncedApiKey
  const hasCredentials = trimmedApiKey.length > 0 || Boolean(integrationId)
  const searchSessionId = useOpaqueCredentialSessionId(debouncedApiKey)
  const queryInput = useMemo(
    () => buildSenderProfilesInput({
      apiKey: debouncedApiKey,
      integrationId,
      providerConfig,
      searchSessionId,
    }),
    [debouncedApiKey, integrationId, providerConfig, searchSessionId],
  )
  const profilesQuery = useDeliveryIntegrationSenderProfilesQuery(
    queryInput,
    hasCredentials && !hasPendingApiKey,
  )
  const selectedProfile = findSelectedProfile(
    profilesQuery.data?.profiles ?? [],
    values.config.sender.providerIdentity,
  )
  const fieldError = senderFieldError(nameMeta, phoneMeta)
  const state = senderProfileState({
    error: profilesQuery.error,
    hasCredentials,
    hasPendingApiKey,
    isFetching: profilesQuery.isFetching,
    result: profilesQuery.data,
  })

  useEffect(() => {
    if (!profilesQuery.data || !values.config.sender.providerIdentity) return
    const profileStillExists = findSelectedProfile(
      profilesQuery.data.profiles,
      values.config.sender.providerIdentity,
    )
    if (profileStillExists) return
    void setFieldValue(
      "config.sender",
      clearSenderProfile(values.config.sender),
      false,
    )
  }, [profilesQuery.data, setFieldValue, values.config.sender])

  function handleSelect(profile: DeliverySenderProfile) {
    void setFieldValue(
      "config.sender",
      applySenderProfile(values.config.sender, profile),
    )
    void setFieldTouched("config.sender.name", true, false)
    void setFieldTouched("config.sender.phone", true, false)
    setOpen(false)
  }

  return (
    <div className="min-w-0 max-w-full space-y-3 overflow-hidden rounded-md border p-4">
      {renderSenderProfileState({
        fieldError,
        onRetry: () => void profilesQuery.refetch(),
        onSelect: handleSelect,
        open,
        selectedProfile,
        setOpen,
        state,
      }, tUi)}
    </div>
  )
}

function renderSenderProfileState(input: {
  fieldError: string | null
  onRetry: () => void
  onSelect: (profile: DeliverySenderProfile) => void
  open: boolean
  selectedProfile: DeliverySenderProfile | null
  setOpen: (open: boolean) => void
  state: SenderProfileState
}, tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderProfileField">>) {
  switch (input.state.kind) {
    case "credentials_required":
      return (
        <SenderProfileStatus
          label={tUi("sender")}
          message={tUi("enterTheNovaPostAPIKeyToLoad")}
        />
      )
    case "loading":
      return (
        <SenderProfileStatus
          label={tUi("sender")}
          message={tUi("loadingSavedNovaPostSenders")}
          loading
        />
      )
    case "error":
      return (
        <div className="space-y-2" aria-live="polite">
          <p className="text-sm font-medium"><span key={tUi("sender")}>{tUi("sender")}</span></p>
          <p className="text-sm text-destructive">{input.state.message}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={input.onRetry}
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span><span key={tUi("tryAgain")}>{tUi("tryAgain")}</span></span>
          </Button>
        </div>
      )
    case "manual":
      return (
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-sm font-medium"><span key={tUi("sender")}>{tUi("sender")}</span></p>
            <p className="text-xs text-muted-foreground">
              <span key={tUi("thisNovaPostAccountHasNoSavedSender")}>{tUi("thisNovaPostAccountHasNoSavedSender")}</span></p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormInput
              name="config.sender.name"
              label={tUi("senderName")}
              required
            />
            <FormInput
              name="config.sender.phone"
              label={tUi("senderPhone")}
              type="tel"
              placeholder="+380…"
              required
            />
          </div>
        </div>
      )
    case "missing_contacts":
      return (
        <div className="space-y-1" aria-live="polite">
          <p className="text-sm font-medium"><span key={tUi("sender")}>{tUi("sender")}</span></p>
          <p className="text-sm text-destructive">
            <span key={tUi("novaPostFoundSavedSenderCounterpartiesButNone")}>{tUi("novaPostFoundSavedSenderCounterpartiesButNone")}</span></p>
        </div>
      )
    case "ready":
      return (
        <SenderProfileCombobox
          fieldError={input.fieldError}
          onSelect={input.onSelect}
          open={input.open}
          profiles={input.state.profiles}
          selectedProfile={input.selectedProfile}
          setOpen={input.setOpen}
        />
      )
    default: {
      const exhaustiveState: never = input.state
      return exhaustiveState
    }
  }
}

function SenderProfileCombobox({
  fieldError,
  onSelect,
  open,
  profiles,
  selectedProfile,
  setOpen,
}: {
  fieldError: string | null
  onSelect: (profile: DeliverySenderProfile) => void
  open: boolean
  profiles: DeliverySenderProfile[]
  selectedProfile: DeliverySenderProfile | null
  setOpen: (open: boolean) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesProvidersNovaPostNovaPostSenderProfileField")

  return (
    <div className="min-w-0 max-w-full space-y-1.5">
      <Label id="nova-post-sender-label"><span key={tUi("sender")}>{tUi("sender")}</span></Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-labelledby="nova-post-sender-label"
            aria-expanded={open}
            aria-required="true"
            aria-invalid={Boolean(fieldError)}
            aria-describedby={fieldError
              ? "nova-post-sender-error"
              : "nova-post-sender-description"}
            className="h-auto min-h-10 w-full min-w-0 max-w-full justify-between overflow-hidden py-2 text-left font-normal"
          >
            <span key={selectedProfile
                ? senderProfileLabel(selectedProfile)
                : tUi("selectASavedSender")} className={cn(
              "min-w-0 flex-1 truncate",
              !selectedProfile && "text-muted-foreground",
            )}>
              {selectedProfile
                ? senderProfileLabel(selectedProfile)
                : tUi("selectASavedSender")}
            </span>
            <ChevronsUpDown
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] overscroll-contain p-0"
        >
          <Command>
            <CommandInput
              placeholder={tUi("searchBySenderCompanyOrPhone")}
              aria-label={tUi("searchSavedSenders")}
              name="nova-post-sender-search"
              autoComplete="off"
            />
            <CommandList>
              <CommandEmpty><span key={tUi("noMatchingSenderFound")}>{tUi("noMatchingSenderFound")}</span></CommandEmpty>
              <CommandGroup>
                {profiles.map(profile => (
                  <CommandItem
                    key={senderProfileKey(profile)}
                    value={senderProfileSearchValue(profile)}
                    onSelect={() => onSelect(profile)}
                    className="items-start py-2"
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        senderCheckOpacity(selectedProfile, profile),
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block break-words">
                        {senderProfileLabel(profile)}
                      </span>
                      <span className="block break-words text-xs text-muted-foreground">
                        {senderProfileDetails(profile)}
                      </span>
                      <span className={cn(
                        "block text-xs",
                        profile.capabilities.supportsCashOnDelivery
                          ? "text-green-700"
                          : "text-muted-foreground",
                      )}>
                        {senderPaymentCapabilityLabel(tUi, profile)}
                      </span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p
        id="nova-post-sender-description"
        className="text-xs text-muted-foreground"
      >
        {senderProfileDescription(selectedProfile, tUi)}
      </p>
      {fieldError ? (
        <p
          id="nova-post-sender-error"
          className="text-xs font-medium text-destructive"
        >
          {fieldError}
        </p>
      ) : null}
    </div>
  )
}

function SenderProfileStatus({
  label,
  loading = false,
  message,
}: {
  label: string
  loading?: boolean
  message: string
}) {
  return (
    <div className="space-y-1.5" aria-live="polite">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex min-h-10 items-center gap-2 rounded-md border bg-muted/30 px-3 text-sm text-muted-foreground">
        {loading ? (
          <Loader2
            className="h-4 w-4 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
        ) : null}
        {message}
      </div>
    </div>
  )
}

type SenderProfileState =
  | { kind: "credentials_required" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "manual" }
  | { kind: "missing_contacts" }
  | { kind: "ready"; profiles: DeliverySenderProfile[] }

function senderProfileState(input: {
  error: Error | null
  hasCredentials: boolean
  hasPendingApiKey: boolean
  isFetching: boolean
  result?: {
    profiles: DeliverySenderProfile[]
    counterpartyCount: number
  }
}): SenderProfileState {
  if (!input.hasCredentials) return { kind: "credentials_required" }
  if (input.hasPendingApiKey) return { kind: "loading" }
  if (input.error) return { kind: "error", message: input.error.message }
  if (input.isFetching || !input.result) {
    return { kind: "loading" }
  }
  if (input.result.counterpartyCount === 0) return { kind: "manual" }
  if (input.result.profiles.length === 0) return { kind: "missing_contacts" }
  return { kind: "ready", profiles: input.result.profiles }
}

function buildSenderProfilesInput(input: {
  apiKey: string
  integrationId?: string
  providerConfig: DeliveryIntegrationConfig["providerConfig"]
  searchSessionId: string
}): ListDeliveryIntegrationSenderProfilesInput {
  if (input.apiKey.length > 0 || !input.integrationId) {
    return {
      provider: "nova_post",
      credentialSource: "draft",
      apiKey: input.apiKey,
      searchSessionId: input.searchSessionId,
      providerConfig: normalizeNovaPostProviderConfigInput(input.providerConfig),
    }
  }

  return {
    provider: "nova_post",
    credentialSource: "integration",
    integrationId: input.integrationId,
  }
}

function findSelectedProfile(
  profiles: DeliverySenderProfile[],
  identity: DeliveryIntegrationConfig["sender"]["providerIdentity"],
): DeliverySenderProfile | null {
  if (!identity) return null
  return profiles.find(profile =>
    profile.providerPartyReference === identity.partyReference
    && profile.providerContactReference === identity.contactReference) ?? null
}

function applySenderProfile(
  sender: DeliveryIntegrationConfig["sender"],
  profile: DeliverySenderProfile,
): DeliveryIntegrationConfig["sender"] {
  return {
    name: profile.name,
    phone: profile.phone,
    countryCode: profile.countryCode,
    ...(profile.email ? { email: profile.email } : {}),
    ...(profile.companyName ? { companyName: profile.companyName } : {}),
    ...(profile.companyTin ? { companyTin: profile.companyTin } : {}),
    providerIdentity: {
      partyReference: profile.providerPartyReference,
      contactReference: profile.providerContactReference,
    },
    location: sender.location,
  }
}

function clearSenderProfile(
  sender: DeliveryIntegrationConfig["sender"],
): DeliveryIntegrationConfig["sender"] {
  return {
    name: "",
    phone: "",
    countryCode: "UA",
    location: sender.location,
  }
}

function senderFieldError(
  nameMeta: { touched: boolean; error?: string },
  phoneMeta: { touched: boolean; error?: string },
): string | null {
  if (nameMeta.touched && nameMeta.error) return nameMeta.error
  if (phoneMeta.touched && phoneMeta.error) return phoneMeta.error
  return null
}

function senderProfileKey(profile: DeliverySenderProfile): string {
  return `${profile.providerPartyReference}:${profile.providerContactReference}`
}

function senderProfileLabel(profile: DeliverySenderProfile): string {
  if (!profile.companyName) return profile.name
  return `${profile.name} — ${profile.companyName}`
}

function senderProfileDetails(profile: DeliverySenderProfile): string {
  if (!profile.email) return profile.phone
  return `${profile.phone} · ${profile.email}`
}

function senderProfileDescription(
  profile: DeliverySenderProfile | null,tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderProfileField">>
): string {
  if (!profile) {
    return tUi("chooseTheNovaPostAccountSenderAndContact")
  }
  const paymentCapability = senderPaymentCapabilityLabel(tUi, profile)
  return tUi("theSelectedContactPhoneValue0IsSavedAutomatically", { value0: String(profile.phone), value1: String(paymentCapability) })
}

function senderPaymentCapabilityLabel(tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesProvidersNovaPostNovaPostSenderProfileField">>, profile: DeliverySenderProfile): string {
  return profile.capabilities.supportsCashOnDelivery
    ? tUi("novaPostPaymentOnDeliveryIsEnabled")
    : tUi("novaPostPaymentOnDeliveryIsNotEnabled")
}

function senderCheckOpacity(
  selectedProfile: DeliverySenderProfile | null,
  profile: DeliverySenderProfile,
): string {
  if (!selectedProfile) return "opacity-0"
  if (senderProfileKey(selectedProfile) === senderProfileKey(profile)) {
    return "opacity-100"
  }
  return "opacity-0"
}

function senderProfileSearchValue(profile: DeliverySenderProfile): string {
  return [
    senderProfileKey(profile),
    profile.name,
    profile.companyName,
    profile.companyTin,
    profile.phone,
    profile.email,
  ].filter(Boolean).join(" ")
}
